// src/utils/defect-report-template.js — DropPin client-side defect report.
// ---------------------------------------------------------------------
// Ported from InSpec's api/shared/defect-report-template.js (v2.34.88,
// 499 lines). InSpec's was server-side HTML→CloudConvert; DropPin
// renders client-side with jsPDF for instant preview + download.
//
// Shape bridge: InSpec source read PascalCase mssql rows (d.Description,
// d.MarkX, d.PhotosJson). DropPin API returns camelCase JSON
// (defect.description, defect.markX, defect.photos[]). The translation
// happens inside the template itself so the call site stays clean.
//
// Inputs (all camelCase, as returned by /api/projects, /api/drawings,
// /api/defects, /api/defect-photos):
//   project    { id, name, address }
//   drawings   [{ id, drawingNumber, title, discipline, fileDataUrl }]
//   defects    [{ id, defectNumber, description, priority, status,
//                 assignedTo, locationLabel, drawingId, markX, markY,
//                 createdAt, updatedAt, photos: [{ url, ... }] }]
//   brand      shell options (brandName, brandColor, logoDataUrl, ...)
//   defectIdsFilter  optional Array<number> — pre-filter to a subset
//                    (used by RegisterScreen's "Selected to PDF").
//
// Returns: a jsPDF instance ready to .save() / .output('blob') / etc.
//
// v0.7.0 — initial port.
// v0.9.0 — settings-driven build:
//          • buildReport now accepts { settings } - filters + section toggles
//            + paper size + client name + report title. Filtering happens
//            BEFORE the PDF builds.
//          • Pin colours moved from status-based to trade-based (tradeRgb).
//          • Drawing thumbnail in defect block + plan-section page now uses
//            rasterUrl|sourceUrl from the drawing record (was an undefined
//            fileDataUrl alias - v0.7.0 subagent flagged this).
//          • PaperSize 'a4' | 'letter' selects the jsPDF format string.
//          • client name + custom title prints on the cover.

import { jsPDF } from 'jspdf';
import { PdfShell, fmtDate, hexToRgb, DEFAULT_BRAND } from './pdf-shell.js';
// v0.9.0 — trade catalogue drives pin colours in the report PDF (was
// status-based). Catalogue + helpers live in src/utils/trades.js.
import { tradeRgb, tradeLabel } from './trades.js';
// 2026-09-15 — the plan's markup (measured areas, notes, the Legend stamp) on the
// PDF's plan page, as drawing instructions.
import { markupPdfOps } from '../markup/pdfMarkup.mjs';
// v0.103.0 — report layout styles. Six presentation styles; 'signature'
// (the default) is the pre-style pipeline untouched. Style changes
// PRESENTATION only — same data in every layout.
import { resolveReportStyle } from './report-styles.js';
import { isEmbeddable } from './reportImages.js';
import { normaliseStatus, statusLabel as sharedStatusLabel, statusColors as sharedStatusColors, statusCounts } from './defectStatus.js';

// ---- status / priority helpers --------------------------------------
// v0.14.0 - 3-state workflow: open / rectified / complete.
//   Open       → not yet fixed
//   Rectified  → supervisor fixed it, awaiting owner sign-off
//   Complete   → owner has confirmed the rectification (terminal)
// Legacy values fold in: 'in_progress' / 'new' → open; 'closed' →
// complete. The Status column hasn't changed type, just the set of
// values it can take.
function statusKey(s) {
  return String(s || '').toLowerCase().replace(/[\s-]+/g, '_');
}
// v0.312.0 — labels and colours come from defectStatus.js (On hold and
// Cancelled added), as rgb for jsPDF.
function statusLabel(s) {
  return sharedStatusLabel(s);
}
function statusColors(s) {
  const c = sharedStatusColors(s);
  const rgb = (hex) => { const o = hexToRgb(hex); return [o.r, o.g, o.b]; };
  return { bg: rgb(c.bg), fg: rgb(c.fg), pin: rgb(c.pin) };
}
function prioColors(p) {
  const k = String(p || '').toLowerCase();
  if (k === 'urgent' || k === 'high') return { bg: [254, 226, 226], fg: [153, 27, 27] };
  if (k === 'low')                    return { bg: [219, 234, 254], fg: [30, 64, 175] };
  return                                     { bg: [243, 244, 246], fg: [55, 65, 81] };
}

// Choose the best photo URL field. DropPin's /api/defect-photos returns
// .url and optionally .thumbnailUrl; for the PDF we want full quality
// so prefer .url. Tolerates a few legacy field names just in case.
//
// v0.215.0 — IMAGE DATA ONLY, NEVER AN ADDRESS. Brett: "download pdf doesnt give
// me any images". This returned signed web links, and jsPDF, given a link,
// fetches it itself — after unescape()ing the signature so storage refuses it,
// and through cross-site rules that only allow one of the app's addresses — and
// when that fails it embeds the link TEXT as the JPEG: a bordered empty box, no
// error. The pictures are now fetched and prepared up front by
// loadReportImages (reportImages.js), and anything that is not already image
// data is refused here, so no address can reach jsPDF again.
function photoImage(p) {
  if (!p) return null;
  if (p.pdfImage && isEmbeddable(p.pdfImage.dataUrl)) return p.pdfImage.dataUrl;
  // A photo stored inline long ago (data: URL) is already image data.
  for (const v of [p.displayUrl, p.url, p.dataUrl, p.PhotoDataUrl, p.data, p.thumbnailUrl]) {
    if (isEmbeddable(v)) return v;
  }
  return null;
}
const pickPhotoUrl = photoImage;

/** The plan's picture as image data, or null. Never the PDF, never an address. */
function planImage(dr) {
  if (!dr) return null;
  if (dr.pdfImage && isEmbeddable(dr.pdfImage.dataUrl)) return dr.pdfImage.dataUrl;
  for (const v of [dr.fileDataUrl, dr.rasterUrl]) if (isEmbeddable(v)) return v;
  return null;
}

/**
 * A photo drawn at its OWN shape inside its cell, centred on a pale ground.
 * Photos were stretched to the cell, so a portrait photo in a square cell came
 * out squashed; in a grout report that makes square tiles look oblong. The
 * size comes from loadReportImages; without it (an old inline photo) the cell
 * is filled as before.
 */
function addPhotoInCell(doc, p, url, x, y, w, h) {
  const iw = p && p.pdfImage && p.pdfImage.width, ih = p && p.pdfImage && p.pdfImage.height;
  if (!(iw > 0 && ih > 0)) { doc.addImage(url, imageFormat(url), x, y, w, h, undefined, 'FAST'); drawPairTag(doc, p, x, y); return; }
  const k = Math.min(w / iw, h / ih), dw = iw * k, dh = ih * k;
  doc.setFillColor(243, 244, 246);
  doc.rect(x, y, w, h, 'F');
  doc.addImage(url, imageFormat(url), x + (w - dw) / 2, y + (h - dh) / 2, dw, dh, undefined, 'FAST');
  drawPairTag(doc, p, x, y);
}

/**
 * v0.317.0 - "Before" / "After" on a photo that is one of a pair (a fix photo
 * from InSpec's ghost camera and the photo it answers; normaliseDefect puts
 * the pair first and sets pairRole). Same words and colours as the page.
 */
function drawPairTag(doc, p, x, y) {
  const role = p && p.pairRole;
  if (role !== 'before' && role !== 'after') return;
  const label = role === 'after' ? 'After' : 'Before';
  doc.setFontSize(6.5);
  doc.setFont('helvetica', 'bold');
  const tw = doc.getTextWidth(label) + 2.4;
  if (role === 'after') doc.setFillColor(22, 163, 74); else doc.setFillColor(17, 24, 39);
  doc.rect(x + 1, y + 1, tw, 3.6, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text(label, x + 2.2, y + 3.75);
  doc.setFont('helvetica', 'normal');
}

/** jsPDF's format word for a data URL. */
function imageFormat(dataUrl) {
  return /^data:image\/png/i.test(dataUrl) ? 'PNG' : 'JPEG';
}

/**
 * A photo that could not be fetched says so, where it would have been. A blank
 * box is what Brett got, and it reads as "the photo is blank", not "the photo
 * is missing".
 */
function drawMissingPhoto(doc, x, y, w, h) {
  doc.setFillColor(243, 244, 246);
  doc.setDrawColor(209, 213, 219);
  doc.setLineWidth(0.2);
  doc.rect(x, y, w, h, 'FD');
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  const fs = Math.max(5, Math.min(7, w / 5));
  doc.setFontSize(fs);
  const lines = doc.splitTextToSize('Photo could not be loaded', Math.max(4, w - 3));
  const lh = fs * 0.42;
  const top = y + h / 2 - ((lines.length - 1) * lh) / 2 + fs * 0.15;
  lines.forEach((ln, k) => doc.text(ln, x + w / 2 - doc.getTextWidth(ln) / 2, top + k * lh));
}

/**
 * v0.292.0 — the cover's five KPI value colours for the cover palette that is
 * drawn ('cinematic' = pale ink on the dark gradient; 'light' and 'mono' =
 * dark ink on paper). Keyed on the resolved cover, never on the style name.
 */
export function coverKpiColors(coverKey) {
  const dark = coverKey === 'cinematic';
  return dark
    ? { total: [255, 255, 255], open: [248, 113, 113], hold: [196, 181, 253], rectified: [251, 191, 36], complete: [134, 239, 172], pct: [253, 186, 116] }
    : { total: [15, 23, 42],    open: [153, 27, 27],   hold: [91, 33, 182],   rectified: [146, 64, 14], complete: [21, 128, 61],   pct: [21, 128, 61] };
}

// ---- public entry point ---------------------------------------------
export async function buildDefectReportPdf({
  project,
  drawings = [],
  defects = [],
  brand = {},
  defectIdsFilter = null,
  // v0.40.0 — Per-report Distribution list, rendered on cover.
  reportTeamMembers = [],
  settings = {},
}) {
  // ---- normalise settings + defaults (v0.9.0) ----
  // v0.37.0 — clientName retired. Customer now reads from project.CustomerName.
  //           preparedBy comes through settings (defaulted to current user on
  //           the ReportScreen, editable per report).
  const S = {
    paperSize:    (settings.paperSize === 'letter') ? 'letter' : 'a4',
    title:        settings.title || 'Defect Report',
    preparedBy:   settings.preparedBy || '',
    // v0.41.0 — Three report formats: 'snag-list' (default, everyday
    //           layout), 'client-handover' (rectification evidence +
    //           sign-off), 'subbie-work-order' (grouped by assignee).
    format:       settings.format || 'snag-list',
    // v0.45.30 — Cover visual theme. Forwarded to PdfShell.
    theme:        settings.theme || 'cinematic',
    // v0.103.0 — Report layout style. 'signature' = the legacy output.
    styleKey:     settings.styleKey || 'signature',
    includeCover:        settings.includeCover !== false,        // default true
    includePhotos:       settings.includePhotos !== false,       // default true
    includePlanThumbs:   settings.includePlanThumbs !== false,   // default true
    includeSignoff:      settings.includeSignoff === true,       // default false
    filters: {
      status:   Array.isArray(settings.filters?.status)   ? settings.filters.status   : null,  // null = all
      priority: Array.isArray(settings.filters?.priority) ? settings.filters.priority : null,
      trade:    Array.isArray(settings.filters?.trade)    ? settings.filters.trade    : null,
      drawing:  Array.isArray(settings.filters?.drawing)  ? settings.filters.drawing.map(Number) : null,
    },
  };

  // Apply RegisterScreen's "selected only" filter if provided, THEN the
  // settings-panel filters layered on top.
  let filtered = Array.isArray(defects) ? defects.slice() : [];
  if (defectIdsFilter && defectIdsFilter.length) {
    const set = new Set(defectIdsFilter.map(Number));
    filtered = filtered.filter(d => set.has(Number(d.id)));
  }
  // v0.312.0 — compared as canonical keys both sides, so a saved filter
  // holding a legacy 'in_progress' still means Open, and 'closed' rows
  // count as Complete.
  if (S.filters.status   && S.filters.status.length) {
    const want = new Set(S.filters.status.map(normaliseStatus));
    filtered = filtered.filter(d => want.has(normaliseStatus(d.status)));
  }
  if (S.filters.priority && S.filters.priority.length) filtered = filtered.filter(d => S.filters.priority.includes(String(d.priority || 'medium')));
  if (S.filters.trade    && S.filters.trade.length)    filtered = filtered.filter(d => S.filters.trade.includes(String(d.trade || 'general')));
  if (S.filters.drawing  && S.filters.drawing.length)  filtered = filtered.filter(d => d.drawingId != null && S.filters.drawing.includes(Number(d.drawingId)));

  // Defect numbering per drawing — sort by createdAt then index.
  // InSpec used DB Id ordering by CreatedAt; we mirror that.
  // v0.45.5 — Reverted to single-pin builder. RFI multi-pin workflow
  // is on InSpec, not DropPin. Each defect lives on its primary
  // drawing (DrawingId/MarkX/MarkY) only.
  const drawingById = new Map(drawings.map(d => [d.id, d]));
  const byDrawing = new Map();
  for (const d of filtered) {
    if (d.drawingId != null && d.markX != null && d.markY != null) {
      if (!byDrawing.has(d.drawingId)) byDrawing.set(d.drawingId, []);
      byDrawing.get(d.drawingId).push(d);
    }
  }
  // pinNumber map kept simple: defect's global number drives the
  // in-pin label so the plan page and defect card match.

  // v0.14.0 - 3-state counts. Open / Rectified / Complete. "Done" (in
  // the "% Done" KPI) means rectified+complete - any defect that has
  // had work done on it, even if not yet signed off.
  // v0.312.0 — Cancelled items print (marked Cancelled) but are out of
  // Total and % Done; On hold gets its own number only when there is one.
  const counts = statusCounts(filtered);
  const total = counts.total;
  const pctRectified = counts.pct;
  const holdKpi = (color, str) => (counts.on_hold ? [{ label: 'On hold', value: str ? String(counts.on_hold) : counts.on_hold, color }] : []);
  const reportDate = fmtDate(new Date());
  const ref = (project && (project.code || project.id)) ? `#${project.id}` : '';
  const projectName = (project && (project.name || project.Name)) || 'Untitled project';
  // v0.9.0 — settings.title overrides default 'Defect Report'; project name
  // still rides in the header as the secondary line.
  const docTitle = S.title || 'Defect Report';
  // v0.37.0 — Customer reads from Project (per-project field), not from
  //           report settings. The old settings.clientName is gone from
  //           the UI; if an existing SettingsJson still carries one we
  //           silently fall back to it so legacy reports don't regress.
  const customer = (project && (project.CustomerName || project.customerName))
                || settings.clientName  // legacy SettingsJson back-compat
                || '';
  const preparedBy = S.preparedBy || '';
  const location = (project && (project.address || project.Address)) || '';

  // ---- jsPDF instance ------------------------------------------------
  const doc = new jsPDF({ unit: 'mm', format: S.paperSize, orientation: 'portrait' });
  // v0.38.0 — Free-tier reports get a translucent "DROPPIN" watermark
  //           stamped on every body page; Pro suppresses it.
  const tier = (settings && settings.tier) || 'free';
  // v0.103.0 — Resolve the layout style. Signature carries null tokens,
  // which keeps PdfShell on its legacy code paths exactly.
  const style = resolveReportStyle(S.styleKey);
  const shell = new PdfShell(doc, {
    ...DEFAULT_BRAND,
    ...brand,
    // v0.45.30 — Cover theme propagated from settings. Tenant brand
    // (logo/colour) from `brand` still wins for the accent colour,
    // logo, and brandName — the theme only controls the cover
    // layout chrome.
    theme: S.theme,
    styleTokens: style.tokens,
    watermark: tier === 'pro' ? null : 'DROPPIN',
  });
  // Cover KPI value colours follow the cover that is actually drawn
  // (shell.coverKey): the pale set on the dark cinematic cover, the
  // dark-ink set on a light or mono one. v0.292.0 — it used to key on the
  // STYLE, so Signature with the light or mono theme printed its numbers
  // white on a light page (invisible); the web cover had this right.
  const kpiRgb = coverKpiColors(shell.coverKey);
  const coverKpiTotalRgb = kpiRgb.total;
  const coverKpiOpenRgb  = kpiRgb.open;
  const coverKpiRectRgb  = kpiRgb.rectified;
  const coverKpiCompRgb  = kpiRgb.complete;
  const coverKpiPctRgb   = kpiRgb.pct;

  // ---- COVER (page 1) ------------------------------------------------
  // v0.9.0 — guarded by settings.includeCover.
  let coverDrawn = false;
  if (S.includeCover) {
  shell.drawCover({
    docTitle: `${docTitle} — ${projectName}`,
    ref: ref || '',
    mode: 'Project',
    customer,
    location,
    status: 'Active',
    reportDate,
    // v0.37.0 — operator name on the cover.
    preparedBy,
    // v0.40.0 — Distribution list (cover footer panel).
    reportTeamMembers,
    kpis: [
      { label: 'Total',       value: total,           color: coverKpiTotalRgb },
      { label: 'Open',        value: counts.open,      color: coverKpiOpenRgb },
      ...holdKpi(kpiRgb.hold),
      { label: 'Rectified',   value: counts.rectified, color: coverKpiRectRgb },
      { label: 'Complete',    value: counts.complete,  color: coverKpiCompRgb },
      { label: '% Done',      value: pctRectified + '%', color: coverKpiPctRgb },
    ],
  });
  coverDrawn = true;
  } // end includeCover branch

  // ---- BODY (page 2 onwards) ----------------------------------------
  if (coverDrawn) doc.addPage();
  shell.newPage();
  const headerOpts = {
    docType: 'DEFECT REPORT',
    docNumber: ref,
    docTitle,
    reportDate,
  };
  shell.drawPageHeader(headerOpts);

  // ---- BODY (format-aware) ------------------------------------------
  // v0.41.0 — Three report formats. Snag list is the everyday default;
  //           client-handover trims defect detail in favour of
  //           rectification evidence + sign-off; subbie-work-order
  //           groups defects by assignee, one section per trade.
  if (S.format === 'snag-list') {
    // ---- Snapshot KPIs -------------------------------------------------
    shell.heading('Snapshot');
    drawKpiRow(doc, shell, [
      { label: 'Total defects', value: String(total),            color: [17, 24, 39] },
      { label: 'Open',          value: String(counts.open),      color: [153, 27, 27] },
      ...holdKpi([91, 33, 182], true),
      { label: 'Rectified',     value: String(counts.rectified), color: [146, 64, 14] },
      { label: 'Complete',      value: String(counts.complete),  color: [21, 128, 61] },
      { label: '% Done',        value: pctRectified + '%',       color: [21, 128, 61] },
    ]);

    // ---- By drawing summary table -------------------------------------
    shell.ensureSpace(40, headerOpts);
    shell.heading('By drawing');
    if (byDrawing.size === 0) {
      shell.text('No pinned defects yet.', { color: [107, 114, 128] });
      shell.cursorY += 2;
    } else {
      const rows = [...byDrawing.entries()].map(([id, ds]) => {
        const dr = drawingById.get(id);
        return [dr?.drawingNumber || `Drawing #${id}`, dr?.title || '', String(ds.length)];
      });
      // v0.45.5 — unpinned = no primary drawing reference. Single-pin
      // semantics restored.
      const noPin = filtered.filter(d => !(d.drawingId != null && d.markX != null && d.markY != null)).length;
      if (noPin > 0) rows.push(['(unpinned)', '', String(noPin)]);
      drawTable(doc, shell, ['Drawing', 'Title', 'Pinned'], rows, [40, 100, 30], headerOpts);
    }

    // ---- By assigned trade --------------------------------------------
    const subbieAgg = {};
    for (const d of filtered) {
      const key = d.assignedTo || 'Unassigned';
      subbieAgg[key] = (subbieAgg[key] || 0) + 1;
    }
    const topSubs = Object.entries(subbieAgg).sort((a, b) => b[1] - a[1]).slice(0, 5);
    if (topSubs.length) {
      shell.ensureSpace(30, headerOpts);
      shell.heading('By assigned trade');
      drawTable(doc, shell, ['Assignee', 'Items'], topSubs.map(([k, n]) => [k, String(n)]), [130, 40], headerOpts);
    }

    // ---- All defects ---------------------------------------------------
    doc.addPage();
    shell.newPage();
    shell.drawPageHeader(headerOpts);
    shell.heading('All defects');
    if (filtered.length === 0) {
      shell.text('No defects raised on this project yet.', { color: [107, 114, 128] });
    } else {
      for (const d of filtered) {
        drawDefectItem(doc, shell, d, drawingById, headerOpts, S, style);
      }
    }

    // ---- Plan sections (one per drawing with pins) --------------------
    // v0.9.0 — gated by settings.includePlanThumbs.
    if (S.includePlanThumbs) for (const [drawingId, ds] of byDrawing.entries()) {
      const dr = drawingById.get(drawingId);
      if (!dr) continue;
      doc.addPage();
      shell.newPage();
      shell.drawPageHeader(headerOpts);
      // v0.37.0 — Heading layout flipped: nickname BOLD + filename / drawing
      //           number rendered small underneath. Was "<drawingNumber> ·
      //           <title>" all crammed into bold. Cleaner visual hierarchy,
      //           and the bold line is what the reader actually scans for.
      shell.heading(dr.title || dr.name || 'Plan');
      // Small filename / drawing number line directly under the bold. When
      // SourceFileName column ships in v0.37.1 this will render the original
      // upload filename; for now it falls back to drawingNumber.
      const smallLabel = dr.sourceFileName || dr.fileName || dr.drawingNumber || '';
      if (smallLabel) {
        shell.text(smallLabel, { color: [107, 114, 128], size: 9 });
      }
      shell.text(`${dr.discipline || ''}${dr.discipline ? ' · ' : ''}${ds.length} pin${ds.length === 1 ? '' : 's'}`,
        { color: [107, 114, 128], size: 9 });
      shell.cursorY += 2;
      drawPlanWithPins(doc, shell, dr, ds);
    }

  }

  else if (S.format === 'client-handover') {
    // ---- Client handover ----------------------------------------------
    // Lighter on defect detail; emphasises rectification evidence +
    // sign-off block. Same KPI snapshot up top but no per-drawing /
    // per-trade breakdown — the client doesn't care which sparky did
    // what; they want to see what's been resolved.
    shell.heading('Snapshot');
    drawKpiRow(doc, shell, [
      { label: 'Total defects', value: String(total),            color: [17, 24, 39] },
      { label: 'Open',          value: String(counts.open),      color: [153, 27, 27] },
      ...holdKpi([91, 33, 182], true),
      { label: 'Rectified',     value: String(counts.rectified), color: [146, 64, 14] },
      { label: 'Complete',      value: String(counts.complete),  color: [21, 128, 61] },
      { label: '% Done',        value: pctRectified + '%',       color: [21, 128, 61] },
    ]);

    // Only items that have been actioned. Open items aren't relevant
    // to a handover narrative.
    const resolved = filtered.filter(d => {
      const k = statusKey(d.status);
      return k === 'rectified' || k === 'complete' || k === 'closed';
    });

    doc.addPage();
    shell.newPage();
    shell.drawPageHeader(headerOpts);
    shell.heading('Rectification evidence');
    if (resolved.length === 0) {
      shell.text('No items have been rectified yet. This handover report becomes meaningful once the supervisor signs items off.', { color: [107, 114, 128] });
    } else {
      for (const d of resolved) {
        drawDefectItem(doc, shell, d, drawingById, headerOpts, S, style);
      }
    }

    // Sign-off block — always rendered for the handover format,
    // regardless of settings.includeSignoff (that flag is the snag-
    // list toggle).
    shell.ensureSpace(50, headerOpts);
    shell.heading('Sign-off');
    shell.text(
      'By signing below, the client confirms acceptance of the rectified items above and acknowledges the works are at practical completion subject to any outstanding defects listed.',
      { color: [55, 65, 81], size: 10 }
    );
    shell.cursorY += 8;
    // Signature line — drawn as a thin underline below each label.
    const sigY = shell.cursorY + 14;
    doc.setDrawColor(120, 120, 120);
    doc.setLineWidth(0.3);
    doc.line(shell.margin.left, sigY, shell.margin.left + 80, sigY);
    doc.line(shell.margin.left + 96, sigY, shell.margin.left + 156, sigY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(107, 114, 128);
    doc.text('Client signature', shell.margin.left, sigY + 4);
    doc.text('Date', shell.margin.left + 96, sigY + 4);
    shell.cursorY = sigY + 10;
  }

  else if (S.format === 'subbie-work-order') {
    // ---- Subbie work order --------------------------------------------
    // One section per Assignee. Each subbie gets a printable run of
    // their items + photos + plan-thumbnails. No cross-trade KPIs.
    const bySubbie = new Map();
    for (const d of filtered) {
      const key = (d.assignedTo && d.assignedTo.trim()) || 'Unassigned';
      if (!bySubbie.has(key)) bySubbie.set(key, []);
      bySubbie.get(key).push(d);
    }
    // Stable order — alphabetical, Unassigned last.
    const subbieOrder = Array.from(bySubbie.keys()).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });

    if (subbieOrder.length === 0) {
      shell.heading('Work order');
      shell.text('No defects assigned yet.', { color: [107, 114, 128] });
    } else {
      for (let si = 0; si < subbieOrder.length; si++) {
        const subbie = subbieOrder[si];
        const items = bySubbie.get(subbie);
        if (si > 0) {
          // Page-break between subbies so each gets a clean handout.
          doc.addPage();
          shell.newPage();
          shell.drawPageHeader(headerOpts);
        }
        shell.heading(`${subbie}  ·  ${items.length} item${items.length === 1 ? '' : 's'}`);
        for (const d of items) {
          drawDefectItem(doc, shell, d, drawingById, headerOpts, S, style);
        }
      }
    }
  }

  // ---- Footers on every body page (skipping the cover) -------------
  shell.drawAllFooters({ startPage: 2 });

  return doc;
}

// ---------------------------------------------------------------------
// Helpers — block-level drawers. All advance shell.cursorY in place.
// ---------------------------------------------------------------------

function drawKpiRow(doc, shell, kpis) {
  const m = shell.margin;
  const w = shell.contentWidth;
  const cellW = (w - (kpis.length - 1) * 3) / kpis.length;
  const h = 18;
  shell.ensureSpace(h + 4);
  const y = shell.cursorY;
  kpis.forEach((k, i) => {
    const x = m.left + i * (cellW + 3);
    doc.setFillColor(248, 250, 252);
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.2);
    doc.roundedRect(x, y, cellW, h, 2, 2, 'FD');
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.text(String(k.label).toUpperCase(), x + 3, y + 5);
    doc.setTextColor(k.color[0], k.color[1], k.color[2]);
    doc.setFontSize(16);
    doc.text(String(k.value), x + 3, y + 13);
  });
  shell.cursorY = y + h + 4;
}

function drawTable(doc, shell, headers, rows, colWidthsHint, headerOpts) {
  const m = shell.margin;
  const totalHint = colWidthsHint.reduce((a, b) => a + b, 0);
  const scale = shell.contentWidth / totalHint;
  const colWidths = colWidthsHint.map(w => w * scale);
  const rowH = 6.5;

  // Header row
  shell.ensureSpace(rowH + 2, headerOpts);
  doc.setFillColor(243, 244, 246);
  doc.rect(m.left, shell.cursorY, shell.contentWidth, rowH, 'F');
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  let cx = m.left + 2;
  headers.forEach((h, i) => {
    doc.text(String(h).toUpperCase(), cx, shell.cursorY + 4.5);
    cx += colWidths[i];
  });
  shell.cursorY += rowH;
  doc.setDrawColor(248, 113, 80);  // brand-ish accent
  const { r, g, b } = shell.brandRgb;
  doc.setDrawColor(r, g, b);
  doc.setLineWidth(0.4);
  doc.line(m.left, shell.cursorY, m.left + shell.contentWidth, shell.cursorY);

  // Body rows
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(31, 41, 55);
  rows.forEach((row, ri) => {
    shell.ensureSpace(rowH + 1, headerOpts);
    if (ri % 2 === 0) {
      doc.setFillColor(250, 250, 250);
      doc.rect(m.left, shell.cursorY, shell.contentWidth, rowH, 'F');
    }
    let x = m.left + 2;
    row.forEach((cell, i) => {
      const txt = doc.splitTextToSize(String(cell || ''), colWidths[i] - 4);
      doc.text(txt[0] || '', x, shell.cursorY + 4.5);
      x += colWidths[i];
    });
    shell.cursorY += rowH;
    doc.setDrawColor(229, 231, 235);
    doc.setLineWidth(0.1);
    doc.line(m.left, shell.cursorY, m.left + shell.contentWidth, shell.cursorY);
  });
  shell.cursorY += 4;
}

// ---------------------------------------------------------------------
// v0.103.0 — Style dispatcher. Every renderer carries the SAME data —
// defect number, description, photos, priority, status, trade,
// location, plan reference — only the presentation differs.
//   card       → drawDefectBlock (legacy Signature layout, untouched)
//   airy       → Professional: serif, whitespace, hairline separators
//   boldPhoto  → Bold: accent bar, heavy number, big photos
//   row        → Compact: dense one-row-per-defect table
//   modernCard → Modern: soft filled card, dot chips, rounded corners
//   ruled      → Classic: formal ruled block, label/value fields
function drawDefectItem(doc, shell, d, drawingById, headerOpts, S, style) {
  const kind = (style && style.item) || 'card';
  if (kind === 'airy')       return drawDefectItemAiry(doc, shell, d, drawingById, headerOpts, S);
  if (kind === 'boldPhoto')  return drawDefectItemBold(doc, shell, d, drawingById, headerOpts, S);
  if (kind === 'row')        return drawDefectItemRow(doc, shell, d, drawingById, headerOpts, S);
  if (kind === 'modernCard') return drawDefectItemModern(doc, shell, d, drawingById, headerOpts, S);
  if (kind === 'ruled')      return drawDefectItemRuled(doc, shell, d, drawingById, headerOpts, S);
  return drawDefectBlock(doc, shell, d, drawingById, headerOpts, S);
}

// Shared: draw a plan thumbnail with the defect's pin overlaid.
// Returns false when the drawing has no usable image (caller skips
// the reserved column). Hairline border keeps it crisp on white.
function drawPlanThumb(doc, x, y, w, h, dr, d) {
  const img = dr && planImage(dr);
  if (!img) return false;
  try {
    doc.addImage(img, imageFormat(img), x, y, w, h, undefined, 'FAST');
  } catch { return false; }
  if (d.markX != null && d.markY != null) {
    const tc = tradeRgb(d.trade);
    const px = x + Number(d.markX) * w;
    const py = y + Number(d.markY) * h;
    doc.setFillColor(tc.r, tc.g, tc.b);
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.5);
    doc.circle(px, py, 2.2, 'FD');
    const num = d.defectNumber || d.id;
    if (num != null) {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      const tw = doc.getTextWidth(String(num));
      doc.text(String(num), px - tw / 2, py + 1.2);
    }
  }
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.2);
  doc.rect(x, y, w, h);
  return true;
}

// Shared: a row of photo cells. Returns the count actually drawn.
function drawPhotoRow(doc, x, y, photos, maxCount, cellW, cellH, gap, withBorder) {
  let drawn = 0;
  for (let i = 0; i < Math.min(photos.length, maxCount); i++) {
    const url = pickPhotoUrl(photos[i]);
    const px = x + drawn * (cellW + gap);
    if (!url) {
      if (photos[i] && photos[i].pdfImageError) { drawMissingPhoto(doc, px, y, cellW, cellH); drawn++; }
      continue;
    }
    try {
      addPhotoInCell(doc, photos[i], url, px, y, cellW, cellH);
      if (withBorder) {
        doc.setDrawColor(203, 213, 225);
        doc.setLineWidth(0.2);
        doc.rect(px, y, cellW, cellH);
      }
      drawn++;
    } catch { /* skip bad image */ }
  }
  if (photos.length > maxCount && drawn > 0) {
    doc.setTextColor(107, 114, 128);
    doc.setFontSize(7);
    doc.text(`+${photos.length - maxCount} more`, x + drawn * (cellW + gap) - cellW + 1, y + cellH + 3);
  }
  return drawn;
}

// ---- Professional / 'airy' -------------------------------------------
// Serif headline, wide measure, small-caps meta line, photos in a
// modest bordered row, hairline separator. No boxes, no fills.
function drawDefectItemAiry(doc, shell, d, drawingById, headerOpts, S) {
  const m = shell.margin;
  const num = d.defectNumber || d.id;
  const dr = d.drawingId != null ? drawingById.get(d.drawingId) : null;
  const photos = (S.includePhotos && Array.isArray(d.photos)) ? d.photos : [];
  const thumbW = 30;
  const hasThumb = !!(S.includePlanThumbs && dr && planImage(dr) && d.markX != null && d.markY != null);
  const descW = shell.contentWidth - (hasThumb ? thumbW + 6 : 0);

  doc.setFont('times', 'normal');
  doc.setFontSize(10.5);
  const descLines = doc.splitTextToSize(d.description || '(no description)', descW).slice(0, 6);
  const photoH = photos.length ? 26 : 0;
  const estH = 9 + descLines.length * 4.6 + 7 + (photoH ? photoH + 4 : 0) + 7;
  shell.ensureSpace(Math.max(estH, hasThumb ? thumbW + 14 : 0), headerOpts);
  const top = shell.cursorY;

  if (hasThumb) {
    drawPlanThumb(doc, m.left + shell.contentWidth - thumbW, top + 1, thumbW, thumbW, dr, d);
  }

  // Headline: "No. 12" in serif + status/priority/trade in quiet caps.
  let y = top + 5;
  doc.setTextColor(17, 24, 39);
  doc.setFont('times', 'bold');
  doc.setFontSize(12);
  const numStr = `No. ${num != null ? num : '—'}`;
  doc.text(numStr, m.left, y);
  const numW = doc.getTextWidth(numStr);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(107, 114, 128);
  const tagBits = [statusLabel(d.status), d.priority ? String(d.priority) : null, tradeLabel(d.trade)].filter(Boolean);
  doc.text(tagBits.join('  ·  ').toUpperCase(), m.left + numW + 4, y);

  // Description — generous serif paragraph.
  y += 6;
  doc.setTextColor(31, 41, 55);
  doc.setFont('times', 'normal');
  doc.setFontSize(10.5);
  doc.text(descLines, m.left, y);
  y += descLines.length * 4.6 + 3;

  // Meta line — small caps, includes plan reference + location.
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(107, 114, 128);
  const metaBits = [];
  if (d.locationLabel) metaBits.push(`Location: ${d.locationLabel}`);
  if (dr && dr.drawingNumber) metaBits.push(`Plan: ${dr.drawingNumber}`);
  metaBits.push(`Assigned: ${d.assignedTo || 'Unassigned'}`);
  metaBits.push(`Raised ${fmtDate(d.createdAt)}`);
  doc.text(metaBits.join('   ·   ').toUpperCase(), m.left, y);
  y += 4;

  if (photos.length) {
    drawPhotoRow(doc, m.left, y, photos, 3, 26, photoH, 2, true);
    y += photoH + 4;
  }

  y = Math.max(y, hasThumb ? top + thumbW + 4 : y);
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.2);
  doc.line(m.left, y + 2, m.left + shell.contentWidth, y + 2);
  shell.cursorY = y + 8;
}

// ---- Bold / 'boldPhoto' ----------------------------------------------
// Heavy accent bar down the left edge, oversized defect number, solid
// status chip, and BIG photos — up to two per item at near half-page
// width. For reports that lead with the evidence.
function drawDefectItemBold(doc, shell, d, drawingById, headerOpts, S) {
  const m = shell.margin;
  const { r, g, b } = shell.brandRgb;
  const num = d.defectNumber || d.id;
  const dr = d.drawingId != null ? drawingById.get(d.drawingId) : null;
  const sc = statusColors(d.status);
  const photos = (S.includePhotos && Array.isArray(d.photos)) ? d.photos : [];
  const barW = 2.8;
  const padL = barW + 4;
  const thumbW = 34;
  const hasThumb = !!(S.includePlanThumbs && dr && planImage(dr) && d.markX != null && d.markY != null);
  const descW = shell.contentWidth - padL - (hasThumb ? thumbW + 5 : 0);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  const descLines = doc.splitTextToSize(d.description || '(no description)', descW).slice(0, 5);
  const photoH = photos.length ? 46 : 0;
  const textH = 9 + descLines.length * 4.8 + 6;
  const blockH = Math.max(textH, hasThumb ? thumbW + 6 : 0) + (photoH ? photoH + 4 : 0) + 4;
  shell.ensureSpace(blockH + 4, headerOpts);
  const top = shell.cursorY;

  // Accent bar spans the whole block.
  doc.setFillColor(r, g, b);
  doc.rect(m.left, top, barW, blockH, 'F');

  if (hasThumb) {
    drawPlanThumb(doc, m.left + shell.contentWidth - thumbW, top + 1, thumbW, thumbW, dr, d);
  }

  // Number + chips.
  let y = top + 7;
  let x = m.left + padL;
  doc.setTextColor(r, g, b);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  const numStr = `#${num != null ? num : '—'}`;
  doc.text(numStr, x, y);
  x += doc.getTextWidth(numStr) + 4;
  // Solid status chip — filled with the status pin colour, white text.
  doc.setFontSize(8);
  const stText = statusLabel(d.status).toUpperCase();
  const stW = doc.getTextWidth(stText) + 5;
  doc.setFillColor(sc.pin[0], sc.pin[1], sc.pin[2]);
  doc.roundedRect(x, y - 4, stW, 5.2, 1, 1, 'F');
  doc.setTextColor(255, 255, 255);
  doc.text(stText, x + 2.5, y - 0.3);
  x += stW + 3;
  if (d.priority) {
    const pc = prioColors(d.priority);
    x = drawPill(doc, x, y - 4, String(d.priority).toUpperCase(), pc.bg, pc.fg) + 3;
  }
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.text(tradeLabel(d.trade).toUpperCase(), x, y - 0.3);

  // Description — larger than any other style.
  y += 6.5;
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(descLines, m.left + padL, y);
  y += descLines.length * 4.8 + 4;

  // Meta line.
  doc.setTextColor(107, 114, 128);
  doc.setFontSize(8);
  const metaBits = [];
  if (d.locationLabel) metaBits.push(d.locationLabel);
  if (dr && dr.drawingNumber) metaBits.push(`Plan ${dr.drawingNumber}`);
  metaBits.push(d.assignedTo || 'Unassigned');
  metaBits.push(`Raised ${fmtDate(d.createdAt)}`);
  doc.text(metaBits.join('  ·  '), m.left + padL, y);
  y += 3;

  // Big photos — the point of this style.
  y = Math.max(y, hasThumb ? top + thumbW + 4 : y);
  if (photos.length) {
    const cellW = (shell.contentWidth - padL - 3) / 2;
    drawPhotoRow(doc, m.left + padL, y + 1, photos, 2, cellW, photoH, 3, false);
    y += photoH + 4;
  }

  shell.cursorY = top + blockH + 5;
}

// ---- Compact / 'row' ---------------------------------------------------
// One dense row per defect — the most items per page. Photo (or plan)
// thumb, two-line description, chips and the plan reference all in a
// single 15mm band with alternating fill.
function drawDefectItemRow(doc, shell, d, drawingById, headerOpts, S) {
  const m = shell.margin;
  const num = d.defectNumber || d.id;
  const dr = d.drawingId != null ? drawingById.get(d.drawingId) : null;
  const sc = statusColors(d.status);
  const pc = prioColors(d.priority);
  const tc = tradeRgb(d.trade);
  const photos = (S.includePhotos && Array.isArray(d.photos)) ? d.photos : [];
  const rowH = 15;
  shell.ensureSpace(rowH + 0.5, headerOpts);
  const top = shell.cursorY;

  // Alternating stripe. Counter rides on the per-build settings object
  // so it never leaks between reports.
  S._rowIdx = (S._rowIdx || 0) + 1;
  if (S._rowIdx % 2 === 1) {
    doc.setFillColor(249, 250, 251);
    doc.rect(m.left, top, shell.contentWidth, rowH, 'F');
  }

  // Columns: num 9 | thumb 13 | desc flex | status 17 | prio 14 | ref 15
  const xNum = m.left + 1;
  const xThumb = m.left + 10;
  const wThumb = 13;
  const xRef = m.left + shell.contentWidth - 15;
  const xPrio = xRef - 15;
  const xStatus = xPrio - 18;
  const xDesc = xThumb + wThumb + 3;
  const wDesc = xStatus - xDesc - 3;

  // Number with a trade-coloured tick.
  doc.setFillColor(tc.r, tc.g, tc.b);
  doc.rect(m.left, top + 1, 0.9, rowH - 2, 'F');
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text(String(num != null ? num : '—'), xNum + 1.5, top + 6);

  // Thumb: first photo wins; else plan thumb with pin.
  let thumbDrawn = false;
  if (photos.length) {
    const url = pickPhotoUrl(photos[0]);
    if (url) {
      try {
        addPhotoInCell(doc, photos[0], url, xThumb, top + 1, wThumb, rowH - 2);
        doc.setDrawColor(203, 213, 225); doc.setLineWidth(0.2);
        doc.rect(xThumb, top + 1, wThumb, rowH - 2);
        thumbDrawn = true;
      } catch { /* skip */ }
    }
  }
  if (!thumbDrawn && S.includePlanThumbs && dr) {
    thumbDrawn = drawPlanThumb(doc, xThumb, top + 1, wThumb, rowH - 2, dr, d);
  }

  // Description — two tight lines + one meta line.
  doc.setTextColor(31, 41, 55);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const descLines = doc.splitTextToSize(d.description || '(no description)', wDesc).slice(0, 2);
  doc.text(descLines, xDesc, top + 4.5);
  doc.setTextColor(107, 114, 128);
  doc.setFontSize(6.5);
  const metaBits = [];
  if (d.locationLabel) metaBits.push(d.locationLabel);
  metaBits.push(tradeLabel(d.trade));
  metaBits.push(d.assignedTo || 'Unassigned');
  doc.text(metaBits.join(' · ').slice(0, 90), xDesc, top + descLines.length * 3.4 + 6.2);

  // Status + priority pills, plan ref.
  drawPill(doc, xStatus, top + 3, statusLabel(d.status), sc.bg, sc.fg);
  if (d.priority) drawPill(doc, xPrio, top + 3, String(d.priority), pc.bg, pc.fg);
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.text(String(dr && dr.drawingNumber ? dr.drawingNumber : '—').slice(0, 12), xRef, top + 6);
  if (photos.length > 1) {
    doc.setFontSize(6.5);
    doc.text(`${photos.length} photos`, xRef, top + 10);
  }

  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.1);
  doc.line(m.left, top + rowH, m.left + shell.contentWidth, top + rowH);
  shell.cursorY = top + rowH;
}

// ---- Modern / 'modernCard' ---------------------------------------------
// Soft filled card (no outline), accent number chip, dot-chips for
// status / priority / trade, rounded photo grid. The web-app look on
// paper.
function drawDefectItemModern(doc, shell, d, drawingById, headerOpts, S) {
  const m = shell.margin;
  const { r, g, b } = shell.brandRgb;
  const num = d.defectNumber || d.id;
  const dr = d.drawingId != null ? drawingById.get(d.drawingId) : null;
  const sc = statusColors(d.status);
  const pc = prioColors(d.priority);
  const tc = tradeRgb(d.trade);
  const photos = (S.includePhotos && Array.isArray(d.photos)) ? d.photos : [];
  const blockH = 42;
  const thumbW = 36;
  const photoColW = 36;
  shell.ensureSpace(blockH + 4, headerOpts);
  const top = shell.cursorY;

  // Soft card — fill only, generous radius, no border.
  doc.setFillColor(248, 250, 252);
  doc.roundedRect(m.left, top, shell.contentWidth, blockH, 3, 3, 'F');

  // Plan thumb (left) — rounded border overlay.
  const hasThumb = !!(S.includePlanThumbs && dr && planImage(dr) && d.markX != null && d.markY != null);
  let textX = m.left + 5;
  if (hasThumb) {
    drawPlanThumb(doc, m.left + 3, top + 3, thumbW, thumbW, dr, d);
    textX = m.left + thumbW + 8;
  }

  // Accent number chip — rounded square, white number.
  let y = top + 7;
  doc.setFillColor(r, g, b);
  doc.roundedRect(textX, y - 4.6, 7, 6, 1.5, 1.5, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const numS = String(num != null ? num : '—');
  doc.text(numS, textX + 3.5 - doc.getTextWidth(numS) / 2, y);
  let cx = textX + 9.5;

  // Dot chips: light pill + coloured dot + dark label.
  const dotChip = (label, dotRgb) => {
    doc.setFontSize(7.5);
    doc.setFont('helvetica', 'bold');
    const w = doc.getTextWidth(label) + 8;
    doc.setFillColor(241, 245, 249);
    doc.roundedRect(cx, y - 4.2, w, 5.4, 2.7, 2.7, 'F');
    doc.setFillColor(dotRgb[0], dotRgb[1], dotRgb[2]);
    doc.circle(cx + 2.6, y - 1.5, 1, 'F');
    doc.setTextColor(51, 65, 85);
    doc.text(label, cx + 4.8, y - 0.4);
    cx += w + 2;
  };
  dotChip(statusLabel(d.status), sc.pin);
  if (d.priority) dotChip(String(d.priority), pc.fg);
  dotChip(tradeLabel(d.trade), [tc.r, tc.g, tc.b]);
  // Plan ref + id, right-aligned.
  doc.setTextColor(148, 163, 184);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  const refStr = `${dr && dr.drawingNumber ? dr.drawingNumber + '  ·  ' : ''}#${d.defectNumber ?? d.id}`;
  doc.text(refStr, m.left + shell.contentWidth - doc.getTextWidth(refStr) - 4, y);

  // Photos — 2×2 grid, right column.
  const hasPhotos = photos.length > 0;
  const photoColX = m.left + shell.contentWidth - photoColW - 3;
  const reserveRight = hasPhotos ? photoColW + 6 : 0;

  // Description.
  const descW = shell.contentWidth - (textX - m.left) - 5 - reserveRight;
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const descLines = doc.splitTextToSize(d.description || '(no description)', descW);
  doc.text(descLines.slice(0, 3), textX, y + 6.5);
  let metaY = y + 6.5 + Math.min(descLines.length, 3) * 4 + 2.5;

  doc.setTextColor(100, 116, 139);
  doc.setFontSize(8);
  const where = d.locationLabel ? `${d.locationLabel}  ·  ` : '';
  doc.text(`${where}${d.assignedTo || 'Unassigned'}  ·  ${fmtDate(d.createdAt)}`, textX, metaY);

  if (hasPhotos) {
    const cell = (photoColW - 2) / 2;
    let drawn = 0;
    for (let i = 0; i < Math.min(photos.length, 4); i++) {
      const url = pickPhotoUrl(photos[i]);
      const col = i % 2, row = Math.floor(i / 2);
      const px = photoColX + col * (cell + 2);
      const py = top + 3 + row * (cell + 2);
      if (!url) {
        if (photos[i] && photos[i].pdfImageError) { drawMissingPhoto(doc, px, py, cell, cell); drawn++; }
        continue;
      }
      try {
        addPhotoInCell(doc, photos[i], url, px, py, cell, cell);
        doc.setDrawColor(226, 232, 240);
        doc.setLineWidth(0.3);
        doc.roundedRect(px, py, cell, cell, 1.5, 1.5, 'S');
        drawn++;
      } catch { /* skip */ }
    }
    if (photos.length > 4 && drawn > 0) {
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(7);
      doc.text(`+${photos.length - 4} more`, photoColX, top + 3 + photoColW + 2.5);
    }
  }

  shell.cursorY = top + blockH + 4;
}

// ---- Classic / 'ruled' ---------------------------------------------------
// Formal ruled block — serif throughout, label/value field grid, media
// row with hairline frames, no fills anywhere. Reads like a schedule
// annexed to a contract.
function drawDefectItemRuled(doc, shell, d, drawingById, headerOpts, S) {
  const m = shell.margin;
  const num = d.defectNumber || d.id;
  const dr = d.drawingId != null ? drawingById.get(d.drawingId) : null;
  const photos = (S.includePhotos && Array.isArray(d.photos)) ? d.photos : [];
  const hasThumb = !!(S.includePlanThumbs && dr && planImage(dr) && d.markX != null && d.markY != null);
  const mediaCount = (photos.length ? Math.min(photos.length, 3) : 0) + (hasThumb ? 1 : 0);
  const mediaH = mediaCount ? 24 : 0;

  doc.setFont('times', 'normal');
  doc.setFontSize(10);
  const descLines = doc.splitTextToSize(d.description || '(no description)', shell.contentWidth).slice(0, 5);
  const estH = 8 + descLines.length * 4.4 + 14 + (mediaH ? mediaH + 4 : 0) + 6;
  shell.ensureSpace(estH, headerOpts);
  const top = shell.cursorY;

  // Opening rule + item heading line.
  doc.setDrawColor(31, 41, 55);
  doc.setLineWidth(0.5);
  doc.line(m.left, top, m.left + shell.contentWidth, top);
  let y = top + 5.5;
  doc.setTextColor(17, 24, 39);
  doc.setFont('times', 'bold');
  doc.setFontSize(10);
  doc.text(`ITEM NO. ${num != null ? num : '—'}`, m.left, y);
  doc.setFont('times', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(82, 82, 82);
  const refStr = dr && dr.drawingNumber ? `Plan reference: ${dr.drawingNumber}` : '';
  if (refStr) doc.text(refStr, m.left + shell.contentWidth - doc.getTextWidth(refStr), y);

  // Description paragraph.
  y += 5.5;
  doc.setTextColor(31, 41, 55);
  doc.setFont('times', 'normal');
  doc.setFontSize(10);
  doc.text(descLines, m.left, y);
  y += descLines.length * 4.4 + 2.5;

  // Field grid — two columns of Label: Value in serif.
  const fields = [
    ['Location',  d.locationLabel || '—'],
    ['Trade',     tradeLabel(d.trade)],
    ['Status',    statusLabel(d.status)],
    ['Priority',  d.priority ? String(d.priority) : '—'],
    ['Assigned',  d.assignedTo || 'Unassigned'],
    ['Raised',    fmtDate(d.createdAt) || '—'],
  ];
  const colW = shell.contentWidth / 2;
  fields.forEach((f, i) => {
    const col = i % 2, row = Math.floor(i / 2);
    const fx = m.left + col * colW;
    const fy = y + row * 4.6;
    doc.setFont('times', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(115, 115, 115);
    doc.text(`${f[0].toUpperCase()}:`, fx, fy);
    doc.setFont('times', 'normal');
    doc.setFontSize(9.5);
    doc.setTextColor(23, 23, 23);
    doc.text(String(f[1]).slice(0, 48), fx + 20, fy);
  });
  y += Math.ceil(fields.length / 2) * 4.6 + 2;

  // Media row — plan thumb first (with pin), then photos, all framed.
  if (mediaCount) {
    let mx = m.left;
    if (hasThumb) {
      drawPlanThumb(doc, mx, y, 24, mediaH, dr, d);
      mx += 24 + 2;
    }
    if (photos.length) drawPhotoRow(doc, mx, y, photos, 3, 24, mediaH, 2, true);
    y += mediaH + 3;
  }

  // Closing hairline.
  doc.setDrawColor(163, 163, 163);
  doc.setLineWidth(0.15);
  doc.line(m.left, y + 1, m.left + shell.contentWidth, y + 1);
  shell.cursorY = y + 7;
}

function drawDefectBlock(doc, shell, d, drawingById, headerOpts, S) {
  // v0.9.0 — default-fill S so callers from earlier versions don't blow up.
  if (!S) S = { includePhotos: true, includePlanThumbs: true };
  const m = shell.margin;
  const sc = statusColors(d.status);
  const pc = prioColors(d.priority);
  // v0.45.5 — Defect number stays the global one (defectNumber || id).
  //           Reverted multi-pin logic; defects have a single primary
  //           pin only.
  const num = d.defectNumber || d.id;
  const dr = d.drawingId != null ? drawingById.get(d.drawingId) : null;

  // v0.13.1 - layout shape:
  //   [plan thumb 36mm] [description + meta] [photo strip 36mm]
  // Photos used to render inline below the meta text, which collided
  // visually with the description on a busy card and made the layout
  // feel cramped. Brett: "Shift photo thumbnails off to the side."
  // The photo column is a 36×36mm box on the right edge of the card,
  // mirroring the plan-thumb column on the left.
  const blockMinH = 42;
  shell.ensureSpace(blockMinH + 4, headerOpts);
  const top = shell.cursorY;
  const thumbW = 36;
  const thumbH = 36;
  const photoColW = 36;

  // Card outline
  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.2);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(m.left, top, shell.contentWidth, blockMinH, 2, 2, 'FD');

  // Left thumbnail (only if pinned with a drawing that has a raster URL).
  // v0.9.0 — read rasterUrl (preferred) || sourceUrl; the v0.7.0 code read
  // a never-populated dr.fileDataUrl which is why thumbnails never rendered.
  // Skip entirely when settings.includePlanThumbs is off.
  const drImg = dr && planImage(dr);
  let textX = m.left + 4;
  // 2026-09-15 — whether a thumbnail is ACTUALLY on the card. The text below used
  // to decide its indent from a different test (does the drawing carry a
  // fileDataUrl?) than the thumbnail used to draw itself (an image, a pin, and
  // plan thumbnails turned on), so the two disagreed: an unpinned defect's text
  // sat beside an empty gap, and a drawing without that alias had its text
  // printed over the thumbnail. One flag, set when the image lands, decides both.
  let thumbDrawn = false;
  // v0.45.5 — Single-pin: read coords directly off the defect row.
  if (S.includePlanThumbs && dr && drImg && d.markX != null && d.markY != null) {
    try {
      doc.addImage(drImg, m.left + 2, top + 2, thumbW, thumbH, undefined, 'FAST');
      thumbDrawn = true;
      const tc = tradeRgb(d.trade);
      const px = m.left + 2 + Number(d.markX) * thumbW;
      const py = top + 2 + Number(d.markY) * thumbH;
      doc.setFillColor(tc.r, tc.g, tc.b);
      doc.setDrawColor(255, 255, 255);
      doc.setLineWidth(0.5);
      doc.circle(px, py, 2.5, 'FD');
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(7);
      if (num != null) {
        const w = doc.getTextWidth(String(num));
        doc.text(String(num), px - w / 2, py + 1.4);
      }
    } catch {
      // Bad image data; skip thumb silently.
    }
    if (thumbDrawn) textX = m.left + thumbW + 6;
  }

  // Header row: pin num + status badge + priority badge + drawing ref
  // v0.9.0 — small inline badge follows trade colour.
  let y = top + 6;
  if (num != null) {
    const tc = tradeRgb(d.trade);
    doc.setFillColor(tc.r, tc.g, tc.b);
    doc.circle(textX + 3, y - 1, 3, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    const w = doc.getTextWidth(String(num));
    doc.text(String(num), textX + 3 - w / 2, y + 0.6);
    textX += 9;
  }

  textX = drawPill(doc, textX, y - 3, statusLabel(d.status), sc.bg, sc.fg);
  if (d.priority) textX = drawPill(doc, textX + 2, y - 3, String(d.priority), pc.bg, pc.fg);
  // v0.45.5 — Type pill + multi-pin pill removed (RFI workflow → InSpec).
  if (dr) {
    doc.setTextColor(107, 114, 128);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`· ${dr.drawingNumber || ''}`, textX + 2, y);
  }
  doc.setTextColor(107, 114, 128);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  const idStr = `#${d.defectNumber ?? d.id}`;
  const idW = doc.getTextWidth(idStr);
  doc.text(idStr, m.left + shell.contentWidth - idW - 4, y);

  // v0.13.1 - Photos live in a right-side column. Reserve the right
  // strip BEFORE laying out the description so descW can be shrunk.
  const photos = (S.includePhotos && Array.isArray(d.photos)) ? d.photos : [];
  const hasPhotos = photos.length > 0;
  const photoColX = m.left + shell.contentWidth - photoColW - 2;
  const reserveRightCol = hasPhotos ? (photoColW + 4) : 0;

  // Description (now narrower so it doesn't crash into the photo column)
  const descX = thumbDrawn ? m.left + thumbW + 6 : m.left + 4;
  const descW = shell.contentWidth - (descX - m.left) - 4 - reserveRightCol;
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  const descLines = doc.splitTextToSize(d.description || '(no description)', descW);
  doc.text(descLines.slice(0, 3), descX, y + 6);
  const descLineH = 4;
  let metaY = y + 6 + Math.min(descLines.length, 3) * descLineH + 2;

  // Meta
  doc.setTextColor(107, 114, 128);
  doc.setFontSize(8);
  const where = d.locationLabel ? `Where: ${d.locationLabel}  ·  ` : '';
  const who   = `Assigned: ${d.assignedTo || 'Unassigned'}`;
  doc.text(where + who, descX, metaY);
  metaY += 3.5;
  const raised = `Raised ${fmtDate(d.createdAt)}${d.updatedAt && d.updatedAt !== d.createdAt ? ' · Updated ' + fmtDate(d.updatedAt) : ''}`;
  doc.text(raised, descX, metaY);
  metaY += 3.5;

  // v0.13.1 - Photo column on the RIGHT. Up to 4 photos in a 2x2 grid
  // inside a 36x36mm box, anchored to the top-right of the card so it
  // sits alongside the plan thumb (which is anchored top-left).
  if (hasPhotos) {
    const cellSize = (photoColW - 2) / 2;  // 2x2 grid, 2mm gutter
    const startY = top + 3;
    let drawn = 0;
    for (let i = 0; i < Math.min(photos.length, 4); i++) {
      const p = photos[i];
      const url = pickPhotoUrl(p);
      const col = i % 2;
      const row = Math.floor(i / 2);
      const px = photoColX + col * (cellSize + 2);
      const py = startY + row * (cellSize + 2);
      if (!url) {
        if (p && p.pdfImageError) { drawMissingPhoto(doc, px, py, cellSize, cellSize); drawn++; }
        continue;
      }
      try {
        addPhotoInCell(doc, p, url, px, py, cellSize, cellSize);
        doc.setDrawColor(229, 231, 235);
        doc.setLineWidth(0.1);
        doc.rect(px, py, cellSize, cellSize);
        drawn++;
      } catch { /* skip bad image */ }
    }
    if (photos.length > 4) {
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(7);
      doc.text(`+${photos.length - 4} more`, photoColX, startY + photoColW + 3);
    }
    if (drawn === 0) {
      doc.setTextColor(107, 114, 128);
      doc.setFontSize(7);
      doc.text(`${photos.length} photo${photos.length === 1 ? '' : 's'} (couldn't render)`, photoColX, startY + 4);
    }
  }
  // (no "No photos" placeholder — empty space on the right reads cleaner
  // than a stray label.)

  shell.cursorY = top + blockMinH + 3;
}

// v0.45.2 — multi-pin aware. `pinsOnDrawing` is the array byDrawing.get(id)
// produced by the new builder above — each entry is a cloned defect with
// its specific pin's coords overlaid. The in-pin label is the defect's
// global number so the same number lands on every drawing the defect
// touches (reader can cross-reference pins across plans).
function drawPlanWithPins(doc, shell, dr, pinsOnDrawing) {
  // v0.9.0 — accept rasterUrl OR sourceUrl OR legacy fileDataUrl.
  const drImg = dr && planImage(dr);
  if (!drImg) {
    shell.text(dr && dr.pdfImageError
      ? `The plan image could not be loaded: ${dr.pdfImageError}.`
      : 'Drawing image not available in this snapshot.', { color: [107, 114, 128] });
    return;
  }
  const m = shell.margin;
  // Reserve a roughly A4 landscape-y portion of the page.
  const planW = shell.contentWidth;
  const planH = Math.min(180, 297 - shell.cursorY - m.bottom - 4);

  // 2026-09-15 — the plan keeps its shape. It was stretched to fill planW × planH,
  // so a landscape plan printed squashed; it is now fitted inside that box by its
  // own proportions (the drawing's stored size, else the image's), centred.
  let natW = Number(dr.width) > 0 ? Number(dr.width) : 0;
  let natH = Number(dr.height) > 0 ? Number(dr.height) : 0;
  if (!(natW > 0 && natH > 0)) {
    if (dr.pdfImage && dr.pdfImage.width > 0 && dr.pdfImage.height > 0) { natW = dr.pdfImage.width; natH = dr.pdfImage.height; }
    else { try { const p = doc.getImageProperties(drImg); natW = p.width; natH = p.height; } catch { natW = 0; natH = 0; } }
  }
  let drawW = planW, drawH = planH;
  if (natW > 0 && natH > 0) {
    const ratio = natW / natH;
    if (planW / planH > ratio) drawW = planH * ratio; else drawH = planW / ratio;
  }
  shell.ensureSpace(drawH);
  const top = shell.cursorY;
  const left = m.left + (planW - drawW) / 2;

  try {
    doc.addImage(drImg, imageFormat(drImg), left, top, drawW, drawH, undefined, 'FAST');
  } catch {
    shell.text('(plan image could not be embedded)', { color: [220, 38, 38] });
    return;
  }

  // 2026-09-15 — Brett: "do the pdf too". The plan's markup — measured areas in
  // their type colours with their m², lines, notes and the Legend stamp — over the
  // plan and under the pins, as in the app and on the shared report. A markup
  // problem costs the overlay, never the report.
  if (dr.markup && natW > 0 && natH > 0) {
    try {
      drawMarkupOnPlan(doc, markupPdfOps(dr.markup, natW, natH), { x: left, y: top, w: drawW, h: drawH, W: natW, H: natH });
    } catch (e) {
      console.warn('[report] the markup could not be drawn on the plan:', e);
    }
  }

  doc.setDrawColor(229, 231, 235);
  doc.setLineWidth(0.2);
  doc.rect(left, top, drawW, drawH);

  // Overlay pins — v0.9.0 trade-coloured, v0.45.2 label = global defect#.
  for (const d of pinsOnDrawing) {
    if (d.markX == null || d.markY == null) continue;
    const tc = tradeRgb(d.trade);
    const px = left + Number(d.markX) * drawW;
    const py = top + Number(d.markY) * drawH;
    doc.setFillColor(tc.r, tc.g, tc.b);
    doc.setDrawColor(255, 255, 255);
    doc.setLineWidth(0.6);
    doc.circle(px, py, 4, 'FD');
    const n = d.defectNumber || d.id;
    if (n != null) {
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      const w = doc.getTextWidth(String(n));
      doc.text(String(n), px - w / 2, py + 1.5);
    }
  }
  shell.cursorY = top + drawH + 4;
}

// 2026-09-15 — draw pdfMarkup.mjs instructions onto a plan placed at `box`
// ({ x, y, w, h } in mm, with the plan's natural pixel size W × H).
const PT_PER_MM = 72 / 25.4;
function drawMarkupOnPlan(doc, ops, box) {
  const X = (nx) => box.x + nx * box.w;
  const Y = (ny) => box.y + ny * box.h;
  const segmentsFrom = (pts) => {
    const out = [];
    for (let i = 1; i < pts.length; i += 1) {
      out.push([X(pts[i][0]) - X(pts[i - 1][0]), Y(pts[i][1]) - Y(pts[i - 1][1])]);
    }
    return out;
  };
  const withFillOpacity = (opacity, draw) => {
    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity }));
    draw();
    doc.restoreGraphicsState();
  };
  doc.setLineJoin('round');

  for (const op of ops) {
    if (op.op === 'poly' && op.pts.length >= 2) {
      const segs = segmentsFrom(op.pts);
      const x0 = X(op.pts[0][0]), y0 = Y(op.pts[0][1]);
      if (op.closed && op.fill && op.fillOpacity > 0) {
        withFillOpacity(op.fillOpacity, () => {
          doc.setFillColor(op.fill[0], op.fill[1], op.fill[2]);
          doc.lines(segs, x0, y0, [1, 1], 'F', true);
        });
      }
      doc.setDrawColor(op.stroke[0], op.stroke[1], op.stroke[2]);
      doc.setLineWidth(0.45);
      doc.lines(segs, x0, y0, [1, 1], 'S', !!op.closed);
    } else if (op.op === 'arrowhead') {
      const ax = X(op.from[0]), ay = Y(op.from[1]), bx = X(op.to[0]), by = Y(op.to[1]);
      const ang = Math.atan2(by - ay, bx - ax);
      const size = 2.2;
      doc.setFillColor(op.color[0], op.color[1], op.color[2]);
      doc.triangle(
        bx, by,
        bx + size * Math.cos(ang + Math.PI - 0.42), by + size * Math.sin(ang + Math.PI - 0.42),
        bx + size * Math.cos(ang + Math.PI + 0.42), by + size * Math.sin(ang + Math.PI + 0.42),
        'F',
      );
    } else if (op.op === 'label') {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(6.5);
      const lh = 2.6, padX = 1.2, padY = 0.9;
      const w = Math.max(...op.lines.map((s) => doc.getTextWidth(s))) + padX * 2;
      const h = op.lines.length * lh + padY * 2;
      const bx = Math.min(X(op.at[0]) + 1.5, box.x + box.w - w);
      const by = Math.max(Y(op.at[1]) - h - 1, box.y);
      doc.setFillColor(op.bg[0], op.bg[1], op.bg[2]);
      doc.roundedRect(bx, by, w, h, 0.8, 0.8, 'F');
      doc.setTextColor(op.ink[0], op.ink[1], op.ink[2]);
      op.lines.forEach((s, i) => doc.text(s, bx + padX, by + padY + lh * (i + 1) - 0.7));
    } else if (op.op === 'text') {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8);
      doc.setTextColor(op.color[0], op.color[1], op.color[2]);
      doc.text(op.text, X(op.at[0]), Y(op.at[1]) + 2.8);
    } else if (op.op === 'legend') {
      drawLegendOp(doc, op, box, withFillOpacity);
    }
  }
}

// The legend is a stamp: its sizes follow the plan (natural px → mm). On a whole
// plan fitted to a page that can make it unreadably small, so its type never
// prints below ~6 pt; it grows from its corner and is kept inside the plan.
function drawLegendOp(doc, op, box, withFillOpacity) {
  const MIN_FONT_MM = 2.1;
  const mmPerPx = Math.max(box.w / box.W, MIN_FONT_MM / op.fsPx);
  const mm = (px) => px * mmPerPx;
  const w = mm(op.wPx), h = mm(op.hPx);
  const x = Math.max(box.x, Math.min(box.x + op.at[0] * box.w, box.x + box.w - w));
  const y = Math.max(box.y, Math.min(box.y + op.at[1] * box.h, box.y + box.h - h));
  const fontPt = (px) => mm(px) * PT_PER_MM;

  doc.setFillColor(255, 255, 255);
  doc.setDrawColor(15, 23, 42);
  doc.setLineWidth(Math.max(0.2, mm(op.fsPx * 0.06)));
  doc.roundedRect(x, y, w, h, mm(op.fsPx * 0.3), mm(op.fsPx * 0.3), 'FD');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(fontPt(op.fsPx * 0.9));
  doc.setTextColor(15, 23, 42);
  doc.text(op.title, x + mm(op.padPx), y + mm(op.padPx + op.titleHPx / 2), { baseline: 'middle' });

  for (const r of op.rows) {
    const cy = y + mm(r.topPx + op.rowHPx / 2);
    const s = mm(op.swatchPx);
    const sx = x + mm(op.padPx), sy = cy - s / 2;
    withFillOpacity(0.35, () => {
      doc.setFillColor(r.color[0], r.color[1], r.color[2]);
      doc.rect(sx, sy, s, s, 'F');
    });
    doc.setDrawColor(r.color[0], r.color[1], r.color[2]);
    doc.setLineWidth(mm(op.fsPx * 0.1));
    doc.rect(sx, sy, s, s, 'S');

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontPt(op.fsPx));
    doc.setTextColor(15, 23, 42);
    doc.text(r.label, x + mm(op.labelXPx), cy, { baseline: 'middle' });
    if (op.showTotals) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontPt(op.fsPx * 0.9));
      doc.setTextColor(51, 65, 85);
      doc.text(r.total, x + mm(op.totalsXPx), cy, { baseline: 'middle', align: 'right' });
    }
  }
}

// Coloured pill helper. Returns the X cursor AFTER the pill.
function drawPill(doc, x, y, text, bg, fg) {
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  const w = doc.getTextWidth(text) + 4;
  doc.setFillColor(bg[0], bg[1], bg[2]);
  doc.roundedRect(x, y, w, 4.5, 1, 1, 'F');
  doc.setTextColor(fg[0], fg[1], fg[2]);
  doc.text(text, x + 2, y + 3.3);
  return x + w;
}
